/**
 * ManageAudienceModal — the focused editor for a SENT invitation's audience.
 *
 * Born from Aleix's 8/31 feedback: editing who's invited used to mean
 * reopening the full creation wizard and reverse-engineering the audience
 * picker. This dialog shows the invitation as it is — every current learner
 * with a Remove action — plus a search box to add more. Changes are staged
 * locally and applied on Save: one revoke per removed learner, one /assign
 * (send=true, same schedule/channels/message as the original) for the added
 * ones, so newcomers get the same invitation the originals did.
 */
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon, ArrowUturnLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Modal from '../../components/common/Modal';
import { errorMessageFrom } from '../../api/client';
import { useAssignCourse, useRevokeAssignment } from '../../services/courses';
import { useAssignableLearners } from '../../hooks/useAssignableLearners';
import {
  useLearnerRoles,
  useLearnerDepartments,
  useLearnerLocations,
} from '../../services/learners';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import type { Learner } from '../../types/learner';
import type { AssignmentGroup } from './assignmentGroups';

interface Props {
  courseId: string;
  group: AssignmentGroup;
  onClose: () => void;
}

function initials(l: Learner): string {
  const a = l.first_name?.[0] ?? '';
  const b = l.last_name?.[0] ?? '';
  return (a + b).toUpperCase() || (l.email?.[0] ?? '?').toUpperCase();
}

function displayName(l: Learner): string {
  return l.full_name || `${l.first_name} ${l.last_name}`.trim() || l.email;
}

export default function ManageAudienceModal({ courseId, group, onClose }: Props) {
  const { data: allLearners = [], isLoading: learnersLoading } = useAssignableLearners();
  const revokeAssignment = useRevokeAssignment();
  const assignCourse = useAssignCourse();

  const [toRemove, setToRemove] = useState<Set<string>>(new Set()); // assignment row ids
  const [toAdd, setToAdd] = useState<Set<string>>(new Set()); // learner (user) ids
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  // Adding people one by one was the only way in, even though the audience was
  // very likely built from a label in the first place (Aleix, 2026-09-15).
  const [labelKind, setLabelKind] = useState<'role' | 'department' | 'location' | null>(null);
  const [confirmRemoveAll, setConfirmRemoveAll] = useState(false);
  const { attributeFilters } = usePlanFeatures();
  const { data: roles = [] } = useLearnerRoles();
  const { data: departments = [] } = useLearnerDepartments();
  const { data: locations = [] } = useLearnerLocations();

  const byId = useMemo(() => new Map(allLearners.map((l) => [l.id, l])), [allLearners]);

  // Current recipients, resolved to learner records for display.
  const current = useMemo(
    () =>
      group.rows.map((row) => ({
        row,
        learner: row.learner_id ? byId.get(row.learner_id) : undefined,
      })),
    [group.rows, byId]
  );
  const currentLearnerIds = useMemo(
    () => new Set(group.rows.map((r) => r.learner_id).filter(Boolean) as string[]),
    [group.rows]
  );

  // Add-picker candidates: everyone not already invited and not staged.
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allLearners
      .filter((l) => !currentLearnerIds.has(l.id) && !toAdd.has(l.id))
      .filter(
        (l) =>
          !q ||
          displayName(l).toLowerCase().includes(q) ||
          (l.email ?? '').toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [allLearners, currentLearnerIds, toAdd, search]);

  /**
   * Every label of the chosen kind, with how many NEW people it would invite.
   *
   * Deliberately not filtered down to the ones that would add somebody: in a
   * workspace where few members are labelled, that left the panel empty and
   * looking broken (Aleix, 2026-09-15). A label that adds nobody says so.
   */
  const labelOptions = useMemo(() => {
    if (!labelKind) return [];
    const source =
      labelKind === 'role' ? roles : labelKind === 'department' ? departments : locations;
    const field =
      labelKind === 'role' ? 'role_id' : labelKind === 'department' ? 'department_id' : 'location_id';
    return source.map((label) => {
      const members = allLearners.filter(
        (l) => (l as unknown as Record<string, unknown>)[field] === label.id
      );
      const ids = members
        .map((l) => l.id)
        .filter((id) => !currentLearnerIds.has(id) && !toAdd.has(id));
      // Rows to take out: the invited people who carry this label.
      const memberIds = new Set(members.map((l) => l.id));
      const removable = group.rows
        .filter((r) => r.learner_id && memberIds.has(r.learner_id) && !toRemove.has(r.id))
        .map((r) => r.id);
      return {
        id: label.id,
        name: label.name,
        ids,
        removable,
        total: members.length,
      };
    });
  }, [labelKind, roles, departments, locations, allLearners, currentLearnerIds, toAdd, group.rows, toRemove]);

  const stageLabel = (ids: string[]) =>
    setToAdd((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });

  const removeEveryone = () =>
    setToRemove(new Set(group.rows.map((r) => r.id)));

  const unstageLabel = (rowIds: string[]) =>
    setToRemove((prev) => {
      const next = new Set(prev);
      rowIds.forEach((id) => next.add(id));
      return next;
    });

  const stagedAdds = useMemo(
    () => [...toAdd].map((id) => byId.get(id)).filter(Boolean) as Learner[],
    [toAdd, byId]
  );

  const toggleRemove = (rowId: string) =>
    setToRemove((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });

  const unstageAdd = (learnerId: string) =>
    setToAdd((prev) => {
      const next = new Set(prev);
      next.delete(learnerId);
      return next;
    });

  const stageAdd = (learnerId: string) =>
    setToAdd((prev) => new Set(prev).add(learnerId));

  const dirty = toAdd.size > 0 || toRemove.size > 0;
  const remainingCount = group.rows.length - toRemove.size + toAdd.size;

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all(
        [...toRemove].map((assignmentId) =>
          revokeAssignment.mutateAsync({ courseId, assignmentId })
        )
      );
      if (toAdd.size > 0) {
        await assignCourse.mutateAsync({
          courseId,
          data: {
            learner_ids: [...toAdd],
            title: group.title ?? undefined,
            start_date: group.start_date ?? undefined,
            end_date: group.end_date ?? undefined,
            // Assignment rows serialize publish_date date-only, but the assign
            // endpoint validates a full datetime — anchor to UTC midnight
            // (same convention as ScheduleEditModal).
            publish_date: group.publish_date
              ? group.publish_date.includes('T')
                ? group.publish_date
                : `${group.publish_date}T00:00:00Z`
              : null,
            channels: group.channels ?? (group.channel ? [group.channel] : undefined),
            invite_message: group.invite_message ?? undefined,
            send: true,
          },
        });
      }
      toast.success(
        [
          toAdd.size > 0 && `${toAdd.size} learner${toAdd.size === 1 ? '' : 's'} invited`,
          toRemove.size > 0 && `${toRemove.size} removed`,
        ]
          .filter(Boolean)
          .join(' · ')
      );
      onClose();
    } catch (err: unknown) {
      toast.error(
        errorMessageFrom(err) ?? 'Could not update the audience. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Manage learners"
      data-testid="manage-audience-modal"
      footer={
        <div className="flex items-center justify-between gap-3 w-full">
          {/* min-w-0 + shrink-0 on the actions: the status line grows when it
              says the invitation is ending, and it was squeezing the buttons
              out of shape (Aleix, 2026-09-15). */}
          <p className="min-w-0 flex-1 text-xs text-text-secondary">
            {!dirty
              ? `${group.rows.length} learner${group.rows.length === 1 ? '' : 's'} invited`
              : remainingCount === 0
                ? 'Nobody left — this invitation ends'
                : `${remainingCount} learner${remainingCount === 1 ? '' : 's'} after saving`}
          </p>
          <div className="flex shrink-0 gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full text-sm font-bold text-text-secondary hover:text-text-primary focus-ring transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() =>
                remainingCount === 0 ? setConfirmRemoveAll(true) : handleSave()
              }
              disabled={!dirty || saving}
              data-testid="save-audience-btn"
              className="min-w-[124px] px-5 py-2 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed focus-ring transition-colors"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <p className="text-sm text-text-secondary">
          This invitation was already sent. Anyone you add here receives the
          same invitation now; anyone you remove loses access to the course
          (their progress is kept).
        </p>

        {/* ── Current learners ─────────────────────────────────────────── */}
        <section aria-label="Current learners">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h3 className="text-xs font-bold uppercase tracking-wide text-text-tertiary">
              Invited ({group.rows.length})
            </h3>
            {/* Ending the whole invitation had no button at all — you had to
                remove people one at a time, and Save then refused because
                nobody was left (Aleix, 2026-09-15). */}
            {group.rows.length > 0 && toRemove.size < group.rows.length && (
              <button
                type="button"
                onClick={removeEveryone}
                data-testid="remove-everyone-btn"
                className="text-xs font-bold text-error-600 dark:text-error-400 hover:underline focus-ring rounded-sm"
              >
                Remove everyone
              </button>
            )}
          </div>
          <ul className="flex flex-col divide-y divide-border-primary/10">
            {current.map(({ row, learner }) => {
              const removing = toRemove.has(row.id);
              return (
                <li key={row.id} className="flex items-center gap-3 py-2.5">
                  <span
                    aria-hidden="true"
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-xs font-bold text-primary-600 dark:text-primary-400 transition-opacity ${removing ? 'opacity-40' : ''}`}
                  >
                    {learner ? initials(learner) : '?'}
                  </span>
                  <div className={`min-w-0 flex-1 transition-opacity ${removing ? 'opacity-40' : ''}`}>
                    <p className={`truncate text-sm font-medium text-text-primary ${removing ? 'line-through' : ''}`}>
                      {learner ? displayName(learner) : 'Unknown learner'}
                    </p>
                    {learner?.email && (
                      <p className="truncate text-xs text-text-secondary">{learner.email}</p>
                    )}
                  </div>
                  {removing ? (
                    <button
                      type="button"
                      onClick={() => toggleRemove(row.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-text-primary border border-border-primary/40 hover:bg-text-primary/[0.06] focus-ring active:scale-[0.97] transition-[background-color,transform] duration-150"
                    >
                      <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                      Undo
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleRemove(row.id)}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold text-error-600 dark:text-error-400 border border-error-500/40 hover:bg-error-500/[0.07] focus-ring active:scale-[0.97] transition-[background-color,transform] duration-150"
                    >
                      Remove
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        {/* ── Staged additions ─────────────────────────────────────────── */}
        {stagedAdds.length > 0 && (
          <section aria-label="Learners to add">
            <h3 className="text-xs font-bold uppercase tracking-wide text-text-tertiary mb-2">
              Will be invited ({stagedAdds.length})
            </h3>
            <ul className="flex flex-wrap gap-2">
              {stagedAdds.map((l) => (
                <li key={l.id}>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/10 py-1 pl-3 pr-1.5 text-xs font-bold text-primary-700 dark:text-primary-300">
                    {displayName(l)}
                    <button
                      type="button"
                      onClick={() => unstageAdd(l.id)}
                      aria-label={`Don't invite ${displayName(l)}`}
                      className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-primary-500/20 focus-ring transition-colors"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── Add learners ─────────────────────────────────────────────── */}
        <section aria-label="Add learners">
          <h3 className="text-xs font-bold uppercase tracking-wide text-text-tertiary mb-2">
            Add learners
          </h3>

          {attributeFilters && (
            <div className="mb-3">
              <div className="flex flex-wrap gap-1.5">
                {(['role', 'department', 'location'] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setLabelKind(labelKind === kind ? null : kind)}
                    aria-pressed={labelKind === kind}
                    className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors focus-ring ${
                      labelKind === kind
                        ? 'bg-primary-500'
                        : 'border border-primary-500/40 text-primary-600 dark:text-primary-400 hover:bg-primary-500/[0.08]'
                    }`}
                  >
                    By {kind}
                  </button>
                ))}
              </div>

              {labelKind && (
                <ul className="mt-2 flex flex-wrap gap-2" data-testid="audience-label-options">
                  {labelOptions.length === 0 ? (
                    <li className="py-1 text-sm text-text-secondary">
                      No {labelKind}s exist yet — create them in Settings →
                      Organization → Learner labels.
                    </li>
                  ) : (
                    labelOptions.map((option) => (
                      <li
                        key={option.id}
                        className="inline-flex items-stretch overflow-hidden rounded-full border border-border-primary/40"
                      >
                        <span className="px-3 py-1.5 text-xs font-bold text-text-primary">
                          {option.name}
                          {option.total === 0 && (
                            <span className="ml-1.5 font-normal text-text-tertiary">
                              nobody carries this yet
                            </span>
                          )}
                        </span>
                        {/* A label is a way in AND a way out: removing a whole
                            department meant clicking each person (Aleix,
                            2026-09-15). */}
                        {option.ids.length > 0 && (
                          <button
                            type="button"
                            onClick={() => stageLabel(option.ids)}
                            title={`Invite the ${option.ids.length} not yet invited`}
                            className="border-l border-border-primary/40 px-2.5 text-xs font-bold text-primary-600 dark:text-primary-400 hover:bg-primary-500/[0.1] focus-ring active:scale-[0.97] transition-[background-color,transform] duration-150"
                          >
                            +{option.ids.length}
                          </button>
                        )}
                        {option.removable.length > 0 && (
                          <button
                            type="button"
                            onClick={() => unstageLabel(option.removable)}
                            title={`Remove the ${option.removable.length} already invited`}
                            className="border-l border-border-primary/40 px-2.5 text-xs font-bold text-error-600 dark:text-error-400 hover:bg-error-500/[0.1] focus-ring active:scale-[0.97] transition-[background-color,transform] duration-150"
                          >
                            −{option.removable.length}
                          </button>
                        )}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          )}
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              aria-label="Search learners to add"
              className="input w-full pl-9"
            />
          </div>
          {learnersLoading ? (
            <p className="py-3 text-sm text-text-secondary">Loading learners…</p>
          ) : candidates.length === 0 ? (
            <p className="py-3 text-sm text-text-secondary">
              {search ? 'No one matches that search.' : 'Everyone is already invited.'}
            </p>
          ) : (
            <ul className="mt-1 flex flex-col divide-y divide-border-primary/10">
              {candidates.map((l) => (
                <li key={l.id} className="flex items-center gap-3 py-2.5">
                  <span
                    aria-hidden="true"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-xs font-bold text-primary-600 dark:text-primary-400"
                  >
                    {initials(l)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-text-primary">{displayName(l)}</p>
                    {l.email && <p className="truncate text-xs text-text-secondary">{l.email}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => stageAdd(l.id)}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold text-primary-600 dark:text-primary-400 border border-primary-500/40 hover:bg-primary-500/[0.08] focus-ring active:scale-[0.97] transition-[background-color,transform] duration-150"
                  >
                    + Add
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      {confirmRemoveAll && (
        <ConfirmDialog
          title="End this invitation?"
          message={
            `All ${group.rows.length} learner${group.rows.length === 1 ? '' : 's'} lose access to `
            + 'this course. Their progress is kept, so nothing is lost if you invite them again.'
          }
          confirmLabel="End invitation"
          confirmVariant="danger"
          onConfirm={() => {
            setConfirmRemoveAll(false);
            void handleSave();
          }}
          onCancel={() => setConfirmRemoveAll(false)}
        />
      )}
    </Modal>
  );
}
